import {
  Button,
  Box,
  Flex,
  ListItem,
  OrderedList,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { RowsPlusBottom, TestTube } from "@phosphor-icons/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { colors } from "theme/foundations/colors";
import { toArray } from "utils/text-utils";
import QAPart, { QAPartType } from "./QAPart";
import QuestionPartEditor from "./QuestionPartEditor";
import { useRerender } from "rerenderer";
import { checkQuestionPartCondition, getConditionFilter, isUnset, prettyPrintCondition } from "./check-condition";

/**
 * Copy a qPart, but remove some properties as they are noise for the AI
 * @param {QAPart} part
 * @returns {QAPart}
 */
function newAnswerPart(part) {
  const blank = { ...part };
  delete blank.options; // remove as its noise for the AI
  //   delete blank.condition; actually, keep it for possible historical views
  return blank;
}

/**
 * Editor for multi-part and table questions.
 * See QAPart for the format.
 * @param {Object} props
 * @param {QAPart[]?} props.answerParts Only set for AI-draft tab. This overrides the questionData.answer
 * @param {Object} props.questionData
 * @param {function(string): void} props.setAnswer json-string of QAPart[]
 * @returns
 */
export default function QuestionPartsList({
  questionData,
  answerParts,
  setAnswer,
  canUseHtml,
  disabled,
  readOnly,
  reason,
  fileInfoList,
  allQuestionDataByRef,
  tags,
}) {
  /** @type {QAPart[]} */
  const parts = questionData.parts;
  // make answer parts from answer string. Note: Need to have a stableobject for smooth editing. We poke it onto questionData._answerParts to allow for update logic to be spread across a few places (see ESG-3286)
  // Specifically questionData._answerParts cn be changed here or in apply-AI-answer
  // Setup initial answer parts
  // Note: answer is first null, then can be a string or stay null. Which complicates the logic here (sorry).
  const [initFromNonNullFlag, setInitFromNonNullFlag] = useState(false);
  const { rerender } = useRerender();
  useEffect(() => {
    if (answerParts) {
      return; // these override
    }
    // only init once (per answer is null / provided)
    if (initFromNonNullFlag && questionData._answerParts) return;
    if (questionData._answerParts?.length && !questionData.answer) {
      return;
    }
    let newAnswerParts = [];
    if (questionData.answer) {
      setInitFromNonNullFlag(true);
      try {
        newAnswerParts = JSON.parse(questionData.answer);
        // console.log(
        //   "initAnswerParts from json",
        //   questionData?.issbReference,
        //   newAnswerParts,
        //   JSON.stringify(newAnswerParts)
        // );
      } catch (e) {
        console.warn("(swallow) QuestionPartsList Error parsing answer", questionData.answer, e);
      }
    }
    // fill in any missing answer parts
    const n = parts.length;
    for (let i = newAnswerParts.length; i < n; i++) {
      const blank = newAnswerPart(parts[i]);
      newAnswerParts.push(blank);
    }
    // console.log(
    //   "initAnswerParts",
    //   questionData?.issbReference,
    //   newAnswerParts,
    //   JSON.stringify(newAnswerParts),
    //   questionData
    // );
    questionData._answerParts = newAnswerParts;
    rerender();
  }, [parts, initFromNonNullFlag, questionData, rerender, answerParts]);
  // if answerParts is set (eg AI Draft tab), use it, otherwise use the questionData._answerParts
  const _answerParts = answerParts || questionData._answerParts || [];
  // Called by the part editors if an answer-part changes -- updates the answer string
  // assumes the part-editors modify the answerParts array in place
  const onChange = useCallback(() => {
    console.log(
      "onChange setAnswer",
      questionData?.issbReference,
      questionData._answerParts,
      JSON.stringify(questionData._answerParts),
      questionData,
      "replaces " + questionData.answer
    );
    setInitFromNonNullFlag(true); // avoid the init re-running if blank-answer -to-> user-answer
    setAnswer(JSON.stringify(questionData._answerParts));
  }, [setAnswer, questionData]);

  // no list wrapping if there is only one part
  if (questionData.parts.length === 1) {
    const qPart = questionData.parts[0];
    const qaPart = _answerParts[0];
    if (!qaPart) return null;
    return (
      <QAPartEditorOrTable
        qPart={qPart}
        qaPart={qaPart}
        onChange={onChange}
        canUseHtml={canUseHtml}
        disabled={disabled}
        readOnly={readOnly}
        reason={reason}
        questionData={questionData}
        fileInfoList={fileInfoList}
        allQuestionDataByRef={allQuestionDataByRef}
        tags={tags}
      />
    );
  }

  return (
    <Flex direction="column" gap={2}>
      <OrderedList styleType="lower-alpha">
        {questionData.parts.map((part, index) => (
          <QuestionPartsListItem
            key={index}
            qPart={part}
            index={index}
            qaPart={_answerParts[index]}
            allQAParts={_answerParts}
            onChange={onChange}
            questionData={questionData}
            fileInfoList={fileInfoList}
            canUseHtml={canUseHtml}
            disabled={disabled}
            readOnly={readOnly}
            reason={reason}
            allQuestionDataByRef={allQuestionDataByRef}
            tags={tags}
          />
        ))}
      </OrderedList>
    </Flex>
  );
}

/**
 *
 * list wrapper and relevance check on QAPartEditorOrTable
 */
function QuestionPartsListItem({
  qPart,
  index,
  qaPart,
  allQAParts,
  onChange,
  canUseHtml,
  disabled,
  readOnly,
  reason,
  questionData,
  fileInfoList,
  allQuestionDataByRef,
  tags,
}) {
  // TODO also implement in the to-text for the AI draft tab
  let answerParts = allQAParts;
  let relevant = true;
  if (qPart.condition) {
    relevant = checkQuestionPartCondition({
      condition: qPart.condition,
      part: qPart,
      answerParts,
      questionData,
      allQuestionDataByRef,
      tags,
    });
  }

  return (
    <ListItem mb={4}>
      {qPart.questionPart !== questionData?.question && (
        <Text mb={1} title={qPart.condition ? qPart.condition + " result: " + relevant : null}>
          {qPart.questionPart}
          {qPart.type === QAPartType.select && qPart.multiple && " (select all that apply)"}
          {qPart.autoCalculated && (
            <span title="The value is not set here - it will be auto-calculated by CDP when the answers are uploaded to the CDP portal">
              {" "}
              (auto-calculated)
            </span>
          )}
        </Text>
      )}
      {!relevant && (
        <Text color="gray.500" fontSize="body.xs" title={"condition: " + qPart.condition + " result: " + relevant}>
          Not applicable
        </Text>
      )}
      {qaPart && relevant && (
        <QAPartEditorOrTable
          qPart={qPart}
          qaPart={qaPart}
          onChange={onChange}
          canUseHtml={canUseHtml}
          disabled={disabled}
          readOnly={readOnly}
          reason={reason}
          questionData={questionData}
          fileInfoList={fileInfoList}
          allQuestionDataByRef={allQuestionDataByRef}
          tags={tags}
        />
      )}
    </ListItem>
  );
}

/** this just chooses between the table and the regular editor */
function QAPartEditorOrTable({
  qPart,
  qaPart,
  onChange,
  canUseHtml,
  disabled,
  readOnly,
  reason,
  questionData,
  fileInfoList,
  allQuestionDataByRef,
  tags,
}) {
  const isTable = qPart.type === QAPartType.table;
  if (isTable)
    return (
      <QuestionPartIsTable
        qPart={qPart}
        qaPart={qaPart}
        onChange={onChange}
        canUseHtml={canUseHtml}
        disabled={disabled}
        readOnly={readOnly}
        reason={reason}
        questionData={questionData}
        fileInfoList={fileInfoList}
        allQuestionDataByRef={allQuestionDataByRef}
        tags={tags}
      />
    );
  return (
    <QuestionPartEditor
      qPart={qPart}
      qaPart={qaPart}
      onChange={onChange}
      canUseHtml={canUseHtml}
      disabled={disabled}
      readOnly={readOnly}
      reason={reason}
      questionData={questionData}
      fileInfoList={fileInfoList}
      allQuestionDataByRef={allQuestionDataByRef}
      tags={tags}
    />
  );
}

function QuestionPartIsTable({
  qPart,
  qaPart,
  onChange,
  canUseHtml,
  disabled,
  readOnly,
  reason,
  questionData,
  fileInfoList,
  allQuestionDataByRef,
  tags,
}) {
  const { rerender } = useRerender();
  // HACK allow rows = csv-string or []
  /** @type {string[]} */
  let qRows = useMemo(() => qPart?.rows && toArray(qPart.rows), [qPart]);
  let columnParts = qPart.columns;
  const isFixedRow = qRows && qRows.length > 0;
  if (isFixedRow) {
    console.log("QuestionPartIsTable", qPart, "isFixedRow", isFixedRow);
    // add the row name as the first column
    columnParts = [{ questionPart: qPart.questionPart || "Row", type: QAPartType.fixed }]?.concat(columnParts);
    console.log("columnParts", columnParts);
  } // ./isFixedRow

  if (!qaPart?.answerValue) {
    qaPart.answerValue = [];
  }
  if (!Array.isArray(qaPart.answerValue)) {
    console.error(
      "QuestionPartIsTable",
      qPart,
      "qaPart",
      qaPart,
      "from ",
      questionData,
      "answerValue is not an array",
      qaPart.answerValue
    );
    qaPart.answerValue = [];
  }
  /** @type {QAPart[][]} */
  let answerRows = qaPart.answerValue;
  // HACK Do the rows depend on other data? e.g. CDP q7.16 has a row per country in CDP q1.7
  // Note: This is for expanding ... See also the row condition filter later ??maybe merge these
  if (isFixedRow && qRows.filter((r) => r.includes("...answer(")).length > 0) {
    const expandedRows = [];
    for (let i = 0; i < qRows.length; i++) {
      const qRow = qRows[i];
      if (qRow.substring(0, 10) !== "...answer(") {
        expandedRows.push(qRow);
        continue;
      }
      const rowCondition = qRow.substring(3, qRow.length); // chop the "..." assume the condition is the rest
      const relevant = checkQuestionPartCondition({
        condition: rowCondition,
        part: columnParts[i],
        answerParts: answerRows,
        questionData,
        allQuestionDataByRef,
        tags,
        uncondensed: true,
      });
      console.log("relevant", relevant);
      if (isUnset(relevant)) {
        expandedRows.push("depends on " + prettyPrintCondition(rowCondition));
      } else if (relevant) {
        if (Array.isArray(relevant) && relevant.length > 0) {
          expandedRows.push(...relevant.flat());
        } else {
          console.warn("QuestionPartIsTable", qPart, "row condition", rowCondition, "relevant", relevant, "qRow", qRow);
          expandedRows.push(rowCondition);
        }
      } else {
        console.log("(skip) row condition", rowCondition, "relevant", relevant, "qRow", qRow);
      }
    }
    qRows = expandedRows;
    console.log("QuestionPartIsTable", qPart, "expandedRows", expandedRows);
    // TODO bug (depending on the order of answering linked Qs): filter the answerRows to only include the relevant rows
    const matchedAnswerRows = [];
    for (let i = 0; i < answerRows.length; i++) {
      const row = answerRows[i];
      if (expandedRows.includes(row[0]?.answerValue)) {
        matchedAnswerRows.push(row);
      }
    }
    console.log("matchedAnswerRows", matchedAnswerRows, "from", answerRows);
    answerRows = matchedAnswerRows;
  } // ./ ...answer() expand into rows hack

  // fill in any missing answer parts (Note: idempotent due to starting i so doesnt need useEffect)
  const expectedRowCount = qRows?.length || 0;
  const expectedColumnCount = columnParts.length;

  // fill in the missing rows
  for (let rowIndex = answerRows.length; rowIndex < expectedRowCount; rowIndex++) {
    const rowName = qRows?.[rowIndex];
    // a new row with the first column being the row name or blank
    const newRow = columnParts.map(newAnswerPart);
    if (isFixedRow) newRow[0].answerValue = rowName;
    answerRows.push(newRow);
  }

  // Fill in missing columns for existing rows
  answerRows.forEach((row, rowIndex) => {
    for (let colIndex = row.length; colIndex < expectedColumnCount; colIndex++) {
      const newCol = newAnswerPart(columnParts[colIndex]);
      row.push(newCol);
    }
  });

  console.log("Answerrows", answerRows);
  //   console.log("QuestionPartIsTable", qPart, "qaPart", qaPart, "from ", questionData);

  // Condition check: hide not-applicable rows?
  // Note: done after we fill any blanks (which can copy in the row-label-with-condition)
  // Note: rows with an unset condition will be kept
  const isCondition = answerRows.some((row) => getConditionFilter(row[0]?.answerValue));
  if (isCondition) {
    answerRows = answerRows.map((row) => {
      // is there a condition?
      const rowLabel = row[0]?.answerValue;
      if (!rowLabel) return row;
      const condition = getConditionFilter(rowLabel);
      if (!condition) return row;
      const relevant = checkQuestionPartCondition({
        condition,
        part: row[0],
        answerParts: answerRows,
        questionData,
        allQuestionDataByRef,
        tags,
      });
      if (!relevant) {
        // remove false (but keep unset as well as true)
        return null;
      }
      // remove the condition from the row label (with copying to avoid mutating the original)
      let modRow = [...row];
      modRow[0] = { ...modRow[0] };
      modRow[0].answerValue = rowLabel.replace("[" + condition + "]", "");
      return modRow;
    });
  }
  answerRows = answerRows.filter((row) => row !== null);

  const handleAddRow = () => {
    const aRow = columnParts.map(newAnswerPart);
    answerRows.push(aRow);
    qaPart.answerValue = answerRows;
    // console.log("handleAddRow", questionData, qaPart, "answerRows", answerRows);
    rerender();
    onChange();
  };

  const MAX_COLUMN_WIDTH = "300px";
  /** Note: 160px is approx the width of the word "Environmental", 200px gives a bit more room for selects */
  const MIN_COLUMN_WIDTH = "200px";

  return (
    <TableContainer overflowX="auto">
      <Table variant="striped">
        <Thead>
          <Tr>
            {columnParts.map((part, index) => (
              <Th
                key={index}
                maxW={MAX_COLUMN_WIDTH}
                minW={MIN_COLUMN_WIDTH}
                style={{ whiteSpace: "normal", wordBreak: "break-word" }}
              >
                {part.questionPart} {part.questionPart?.unit && "(" + part.questionPart.unit + ")"}
                {part.autoCalculated && (
                  <span title="The value is not set here - it will be auto-calculated by CDP when the answers are uploaded to the CDP portal">
                    {" "}
                    (auto-calculated)
                  </span>
                )}
              </Th>
            ))}
          </Tr>
        </Thead>
        <Tbody>
          {answerRows.map((row, r) => (
            <Tr key={r}>
              {row.map((part, c) => (
                <Td key={c} maxW={MAX_COLUMN_WIDTH} style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
                  <QuestionPartEditor
                    qPart={columnParts[c]}
                    qaPart={part}
                    onChange={onChange}
                    canUseHtml={canUseHtml}
                    disabled={disabled}
                    readOnly={readOnly}
                    reason={reason}
                    questionData={questionData}
                    fileInfoList={fileInfoList}
                  />
                </Td>
              ))}
            </Tr>
          ))}
        </Tbody>
      </Table>
      {!isFixedRow && (
        <Button onClick={handleAddRow} backgroundColor={colors.lightLime} w="100%" justifyContent="flex-start">
          <RowsPlusBottom size={16} color={colors.limeShade[600]} />{" "}
          <Text as="span" color="limeShade.600">
            &nbsp;Add
          </Text>
          &nbsp;Row
        </Button>
      )}
    </TableContainer>
  );
}
